import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import { HOME_HERO_COPY_BY_LOCALE } from '@/data/shared/homeHeroCopy';
import {
  HOME_PROOF_STATS_BY_LOCALE,
  HOME_PROOF_STAT_LOCALE,
} from '@/data/shared/homeProofStats';

import Area from '@/components/apps/layouts/shared/Area';

import ProofStatCounter from './ProofStatCounter';
import ProofStatEntry from './ProofStatEntry';
import { estimateFigureEm } from './proofStatFigure';
import { useActiveProofStat } from './useActiveProofStat';

/**
 * ### ProofStatsSection
 *
 * The proof behind the claim, as a two-column scroll: the six press-kit
 * figures are scrolled through on the right, one screen each, while the
 * left column stays put and counts up to whichever figure is in the middle
 * of the viewport. The objection the press kit opens with heads the section;
 * the two calls to action close it, once every figure has been seen.
 *
 * The figures and the sentences come from the copy module through
 * {@link HOME_PROOF_STATS_BY_LOCALE}; this component adds no claim of its
 * own.
 *
 * The counter's figure is sized to its column: the column is a container,
 * and the figure's font size is the column's width divided by the figure's
 * estimated width in em, capped at the display size. A long figure in a
 * narrow column is set smaller rather than allowed to run into the entries
 * beside it.
 *
 * @returns The rendered section element
 */
export default function ProofStatsSection() {
  const currentLang = useCurrentLang();
  const stats = HOME_PROOF_STATS_BY_LOCALE[currentLang];
  const locale = HOME_PROOF_STAT_LOCALE[currentLang];
  const { narrative } = HOME_HERO_COPY_BY_LOCALE[currentLang];
  const { activeIndex, setElementAt } = useActiveProofStat(stats.length);
  const active = stats[activeIndex] ?? stats[0];

  return (
    <section
      className="bg-apps-brandGrayBlack"
      data-analytics-section="home_proof_stats"
    >
      <Area>
        <div className="flex flex-col gap-14 py-24 lg:gap-20 lg:py-[clamp(140px,12.5vw,200px)]">
          <p className="max-w-[880px] text-balance text-[28px] font-bold leading-[136%] tracking-[-0.56px] text-apps-coreGrayWhite sm:text-[32px] lg:text-[clamp(38px,3.333vw,48px)] lg:leading-[132%] lg:tracking-[-0.96px]">
            {narrative.lead}
          </p>

          <div className="grid gap-8 2xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] 2xl:gap-[clamp(48px,5.2vw,100px)]">
            <div className="hidden [container-type:inline-size] 2xl:block">
              <div className="sticky top-[calc(50vh-180px)]">
                <p
                  className={
                    'text-[clamp(15px,1.2vw,20px)] font-bold uppercase leading-[140%] tracking-[0.75px] text-apps-brandGray900'
                  }
                >
                  <span className="mr-3 text-apps-brandGray800">
                    {String(activeIndex + 1).padStart(2, '0')} /{' '}
                    {String(stats.length).padStart(2, '0')}
                  </span>
                  {active.kicker}
                </p>
                <p
                  className="mt-7 whitespace-nowrap font-bold leading-[96%] tracking-[-0.06em] text-apps-coreGrayWhite [font-size:min(clamp(80px,8vw,144px),calc(100cqw/var(--figure-em)))] [font-variant-numeric:tabular-nums]"
                  style={
                    {
                      '--figure-em': estimateFigureEm(active.figure),
                    } as React.CSSProperties
                  }
                >
                  <ProofStatCounter stat={active} locale={locale} />
                </p>
                {active.note && (
                  <p
                    className={
                      'mt-8 max-w-105 text-pretty text-[clamp(18px,1.4vw,24px)] font-medium leading-[160%] text-apps-brandGray800'
                    }
                  >
                    {active.note}
                  </p>
                )}
              </div>
            </div>

            <ol className="flex flex-col divide-y divide-[rgba(255,255,255,0.1)] 2xl:divide-y-0">
              {stats.map((stat, index) => (
                <ProofStatEntry
                  key={stat.id}
                  stat={stat}
                  index={index}
                  isActive={index === activeIndex}
                  locale={locale}
                  setElement={setElementAt(index)}
                />
              ))}
            </ol>
          </div>
        </div>
      </Area>
    </section>
  );
}
